import { getCommonBounds } from '../../lib/bound';
import {
  addElement,
  defaultGenericElement,
  reversionElement,
  sortElementsByLayer,
  updateElement,
  type GenericElement,
} from '../../lib/element';
import { prepareElementForExport } from '../../lib/exportImport';
import { history } from '../../lib/history';
import { type Plugin } from '../../lib/plugin';
import { useEditorRuntimeStore } from '../../stores/useEditorRuntimeStore';
import { updateEditorStore, useEditorStore } from '../../stores/useEditorStore';
import { clipToMainImage } from '../../utils/canvas';
import { type Point } from '../../utils/math';
import Perspective from '../../utils/perspective';
import { TEST_STRUCTURE_REAL_SIZE, type GridElement } from '../grid';
import { buildProjectionSegments } from '../grid/utils/projection';
import { type GenericPolyElement } from '../poly';

import {
  clearBooleanCaches,
  clearBooleanCacheForElement,
  DEFAULT_BOOLEAN_BRIGHTNESS,
  DEFAULT_BOOLEAN_CONTRAST,
  getBooleanMask,
} from './utils/masking';
import { computeBooleanShapeData } from './utils/path';

export type BooleanElementGroupType = 'subtract' | 'intersect' | 'union';

export type ElementGroupType = BooleanElementGroupType;

const grayArea = 'rgba(120,120,120, 0.8)';

export interface BooleanElement extends GenericElement {
  type: 'boolean';
  lastUpdateHash: string;
  fillColor: string;
  gridId?: string;
  brightness?: number;
  contrast?: number;
}

interface State {
  fillColor: string;
  loadedPatterns: Record<string, HTMLImageElement>;
}

const state: State = {
  fillColor: '#3c87f7',
  loadedPatterns: {},
};

export type BooleanGroupPlugin = Plugin<BooleanElement> & {
  state: typeof state;
  actions: typeof actions;
};

const actions = {
  createBooleanGroup: (overwrite: Partial<BooleanElement>) => {
    const defaults = defaultGenericElement();
    const newElement: BooleanElement = {
      ...defaults,
      fillColor: state.fillColor,
      contrast: DEFAULT_BOOLEAN_CONTRAST,
      brightness: DEFAULT_BOOLEAN_BRIGHTNESS,
      ...overwrite,
      type: 'boolean',
      lastUpdateHash: '',
    };

    updateEditorStore({ selectedElementIds: [newElement.id] });

    addElement(useEditorStore.getState().elements, newElement);

    history.push(updateEditorStore({ selectedElementIds: [newElement.id] }));
  },
  reevaluateDimensions: (elementId: string) => {
    const elements = useEditorStore.getState().elements;
    const element = elements.get(elementId) as BooleanElement | undefined;
    if (!element) return;

    const elementsInGroup = Array.from(elements.values())
      .filter((el) => el.renderedById === elementId && el.isVisible)
      .sort(sortElementsByLayer);

    const [minX, minY, maxX, maxY] = getCommonBounds(elementsInGroup);

    const isTransforming =
      !!useEditorRuntimeStore.getState().activeTransformation;

    const elementsHash = elementsInGroup
      .map((el) => JSON.stringify(el.version))
      .join('');

    updateElement(elements, element, {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      lastUpdateHash: elementsHash,
      ...(!isTransforming && {
        shape: {
          data: computeBooleanShapeData(elementsInGroup, minX, minY),
          type: 'area' as const,
        },
      }),
      version: {
        ...element.version,
        ...reversionElement(element, ['bounds', 'canvas']).version,
      },
    });
  },
  clearCacheForElement: clearBooleanCacheForElement,
};

const plugin: BooleanGroupPlugin = {
  name: 'boolean',
  cursor: 'default',
  state,
  actions,
  events: {},
  collision: {
    onlyCountDirectElementHitAfterSelection: false,
    bounds: (element) => [
      element.x,
      element.y,
      element.x + element.width,
      element.y + element.height,
      (element.x + element.width) / 2,
      (element.y + element.height) / 2,
    ],
    calculateShape: (element) => element.shape,
    isPointInShape: () => false,
    isPointOnShape: () => false,
  },
  transformation: {
    shiftBySizeOnFlip: false,
    resizedBounds: () => [0, 0, 0, 0, 0, 0],
  },
  render: {
    padding: () => 0,
    draw: ({ context, element, elementState, isExportImage, runtimeState }) => {
      const grid = Array.from(elementState.elements.values()).find(
        (el): el is GridElement =>
          el.type === 'grid' && el.id === `grid-${element.id}`,
      );

      context.save();
      context.lineJoin = 'round';
      context.lineCap = 'round';
      context.lineWidth = 0;

      const elementsInside = Array.from(elementState.elements.values())
        .filter(
          (el): el is GenericPolyElement =>
            el.renderedById === element.id && el.isVisible,
        )
        .sort(sortElementsByLayer);

      if (!elementsInside.length) return context.restore();

      const mask = getBooleanMask(context, element, elementsInside);

      let imageToRender: HTMLImageElement | null = null;
      if (
        grid?.showTestStructure &&
        grid.calculatedTestStructure &&
        grid.calculatedTestStructure.width > 0
      ) {
        imageToRender = grid.calculatedTestStructure;
      } else if (
        grid?.fillMaterial?.image &&
        grid.fillMaterial.image.width > 0
      ) {
        imageToRender = grid.fillMaterial?.image;
      }

      if (grid && imageToRender) {
        if (!isExportImage) {
          context.fillStyle = grayArea;
          context.fillRect(0, 0, element.width, element.height);
        }
        context.save();
        clipToMainImage(context, elementState.elements, element);

        const p = new Perspective(context, imageToRender);
        const scaleFactor = runtimeState.zoom.value;
        const offsetX = (grid.x - element.x) * scaleFactor;
        const offsetY = (grid.y - element.y) * scaleFactor;

        context.scale(1 / scaleFactor, 1 / scaleFactor);

        let repeatsX = 1;
        let repeatsY = 1;

        if (grid.fillMaterial && !grid.showTestStructure) {
          repeatsX = grid.gridScale.x / grid.fillMaterial.textureRealX;
          repeatsY = grid.gridScale.y / grid.fillMaterial.textureRealY;
        } else if (grid.showTestStructure) {
          repeatsX = grid.gridScale.x / TEST_STRUCTURE_REAL_SIZE;
          repeatsY = grid.gridScale.y / TEST_STRUCTURE_REAL_SIZE;
        }

        const segments = buildProjectionSegments(
          grid.points,
          grid.cornerLines,
          0,
          repeatsX,
        );

        const toScenePoint = ([x, y]: Point): Point => [
          x * scaleFactor + offsetX,
          y * scaleFactor + offsetY,
        ];

        for (const { quad, u0, u1 } of segments) {
          const [tl, tr, br, bl] = quad;
          p.draw(
            [
              toScenePoint(tl),
              toScenePoint(tr),
              toScenePoint(br),
              toScenePoint(bl),
            ],
            {
              u0,
              u1,
              v0: 0,
              v1: repeatsY,
            },
          );
        }

        context.restore();
      } else {
        context.save();
        clipToMainImage(context, elementState.elements, element);
        context.fillStyle = element.fillColor;
        context.fillRect(0, 0, element.width, element.height);
        context.restore();
      }

      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.globalCompositeOperation = 'destination-in';
      context.drawImage(mask, 0, 0);
      context.restore();

      context.restore();
    },
    translate: () => undefined,
  },
  selection: {
    hideDefaultTransformHandles: true,
    renderSelectionBorder: () => undefined,
  },
  export: (element) => {
    const prepared = prepareElementForExport(element);
    return prepared;
  },
  import: (element) => {
    const imported = {
      ...defaultGenericElement(),
      ...element,
    } as BooleanElement;

    return imported;
  },
};

export {
  clearBooleanCaches,
  DEFAULT_BOOLEAN_BRIGHTNESS,
  DEFAULT_BOOLEAN_CONTRAST,
};

export const boolean = plugin;
